import axios from 'axios';
import _isEmpty from 'lodash/isEmpty';
import * as types from '../constants/ActionTypes';
import { trackPage } from '../../lib/analytics-helper';
import { mappingRemoteConfig, isStepUpEnabled, isTopicSupportedForRisk } from '../../lib/step-up-helper';
import { fetchFinalTopicContent } from './action-select-topic';
import { loadModalContainer, closeModalContainer } from './action-login-modal';
import { modalChannels, stepUpChannels } from '../constants/UtilVariables';
import { setChatCardClickInSession } from '../../lib/chat-bot-live-helper';
import Page from '../../lib/page';
import { loadChatIneligibleModalContainer } from './action-chat-ineligible-modal';
import { idiChatEligibilityChecker, initializeAppropriateChannel, nonIdiChatEligibilityChecker } from './action-chat';
import { locationUpdate } from './action-contact-location';
import { isChannelAvailableInContactOptions, isNodeChatAlreadyExists } from '../../lib/globalHelper';
import { getIdiContactChannels } from './action-update-user-selection';
import { isSuppressCallUs } from '../../lib/covidHelper';
import { showHideCovidBcpModalAlert } from './action-covid-modal-alert';
import { requestLoggedOutChatValidation } from '../../lib/loggedOutChatHelper';
import { chatBotEligibilityResponse } from './action-chat-bot';

const isIdiContactOptionsEnabled = Page.getIsIDIContactOptions();

const attempts = {
  FIRST: 'first',
  SECOND: 'second',
};

/**
 *  Get selected TopicRefId to perform risk check. TopicRefId is required to perform risk check.
 *  For already logged in user getting topicRefId from state.selectResult
 *  For logged Out to logged in case getting topicRefId from state.smcResultKeeper - which is from session storage.
 *
 * @param state
 * @returns topicRefId || undefined
 */
const getTopicRefId = (state = {}) => {
  const refId =
    ((state.selectResult || {}).topicTreeSelectResult || {}).topicRefId ||
    ((state.smcResultKeeper || {}).topicTreeSelectResult || {}).topicRefId;
  return typeof refId === 'string' ? refId : undefined;
};

export const isLoggedOutToLogin = (state = {}) => !_isEmpty((state.modal || {}).fromChannel);

// TODO:: This is a temporary fix to set proper contact channel for step-up RISK. Needs clean up.
/**
 * In the current implementation, for logged-out to logged-in the fromChannel name is liveChat.
 * so replacing liveChat with chat as RISK will support only chat.
 * Set contactChannel as messageUs when from channel is liveChat || chat and asyncUser
 * @param fromChannel
 * @returns {string}
 */
export const getRiskContactChannelName = (fromChannel) => {
  if ((fromChannel === stepUpChannels.liveChat || fromChannel === stepUpChannels.chat) && Page.isPilotUser()) {
    return stepUpChannels.messageUs;
  }

  if (fromChannel === stepUpChannels.liveChat) {
    return stepUpChannels.chat;
  }
  return fromChannel;
};

// --------------------------- ANW [Auth Node Web]-----------------------------------

export const requestAnwCheck = () => ({ type: types.REQUEST_ANW_CHECK_INITIATED });

export const receivedANWCheck = (anwInfo) => ({
  type: types.RECEIVE_ANW_SUCCESS,
  payload: anwInfo,
});

export const receivedANWError = () => ({ type: types.RECEIVE_ANW_ERROR });

const getContextId = (state = {}) => {
  const authContextId = ((state.stepUp || {}).riskInfo || {}).authflow_context_id;
  return typeof authContextId === 'string' ? authContextId : '';
};

const stepUpRequestTime = (timeStamp) => ({
  type: types.STEP_UP_REQUEST_TIME,
  payload: timeStamp,
});

export const initiateANWCheck = () => (dispatch, getState) => {
  dispatch(requestAnwCheck());
  const state = getState();
  const contextId = getContextId(state);
  const {
    stepUp: { fromChannel },
  } = state;
  const contactChannel = getRiskContactChannelName(fromChannel);
  const anwURL = `/smarthelp/stepup-anw?contextId=${contextId}&contactChannel=${contactChannel}`;

  const timeStamp = new Date().toISOString();
  dispatch(stepUpRequestTime(timeStamp));

  return axios
    .get(anwURL)
    .then((response) => {
      const anwInfo = ((response || {}).data || {}).authNodeWebUrl;
      dispatch(receivedANWCheck(anwInfo));
    })
    .catch(() => {
      trackPage({
        id: 'stepUpIntermediatePage_anwFailure',
        link: `smarthelp-step-up-intermediate|${getTopicRefId(state)}|${contactChannel}|anw-failure`,
      });
      dispatch(receivedANWError());
    });
};

// ----------------------------------- End of ANW -------------------------------------------

// --------------------------------- RISK [ Step Up]-----------------------------------------

export const loadStepUpContainer = () => ({ type: types.LOAD_STEP_UP_CONTAINER });

export const closeStepUpContainer = ({ redirectToChannel }) => ({
  type: types.CLOSE_STEP_UP_CONTAINER,
  payload: redirectToChannel,
});

export const resetStepUp = () => ({ type: types.RESET_STEP_UP });

export const requestRiskCheck = (fromChannel, selectedTopic) => ({
  type: types.REQUEST_RISK_CHECK_INITIATED,
  fromChannel,
  selectedTopic,
});

export const receiveRiskError = () => ({ type: types.RECEIVE_RISK_ERROR });

export const receivedRiskAllow = () => ({ type: types.RECEIVED_RISK_ALLOW });

export const receiveRiskCheck = (riskInfo) => ({
  type: types.RECEIVE_RISK_CHECK,
  payload: riskInfo,
});

/**
 * Service call to perform RISK check.
 * 1) If RISK decision is STEPUP_AUTH open stepup container
 * 2) If RISK decision is STEPUP_AUTH and is from logged out to logged in then open final topic content and stepup container
 * 3) IF RISK decision is ALLOW redirect to the channel where step up is triggered from.
 * 4) Default redirect to the channel where step up is triggered from.
 *
 * @param topicRefId
 * @param fromChannel
 * @param attempt
 * @returns dispatches appropriate actions
 */
export const performStepUpCall = (topicRefId, fromChannel, attempt) => (dispatch, getState) => {
  const {
    persistedStepUp: {
      anw: { isRedirectedToAnw },
    },
  } = getState();

  const riskURL = `/smarthelp/stepup-risk?intentId=${topicRefId}&contactChannel=${fromChannel}&attempt=${attempt}&appEntryPoint=channelSteering`;

  return axios
    .get(riskURL)
    .then((response) => {
      const riskInfo = ((response || {}).data || {}).riskInfo;

      // Check risk decision and perform actions based on that.
      const riskDecision = riskInfo.decisions && riskInfo.decisions.length ? riskInfo.decisions[0].decision_code : '';

      // On Second attempt ignore the decision and fire an FPTI.
      if (attempt === attempts.SECOND) {
        return trackPage({
          id: 'stepUpIntermediatePage_anwIgnoreDecisions',
          link: `smarthelp-step-up-intermediate|${topicRefId}|${fromChannel}|${attempt}|decision-${riskDecision}|anwRedirect-ignoreDecision`,
        });
      }

      // If the attempt is first time perform the below operations.
      dispatch(receiveRiskCheck(riskInfo));

      // Fire FPTI with Decision
      trackPage({
        id: 'stepUpIntermediatePage_decision',
        link: `smarthelp-step-up-intermediate|${topicRefId}|${fromChannel}|${attempt}|decision-${riskDecision}`,
      });

      switch (riskDecision) {
        case 'CONTINGENT': {
          // if the decision is STEP_UP then need to load final intent page for callUs channel before opening step-up modal window.
          if ((isLoggedOutToLogin(getState()) || isRedirectedToAnw) && fromChannel === stepUpChannels.call) {
            const { topic } = getState().persistedStepUp;
            dispatch(fetchFinalTopicContent({ topic }));
          }

          // If the decision is contingent and if there is no translations fire an FPTI and redirect to ANW page.
          if (!isTopicSupportedForRisk(topicRefId)) {
            trackPage({
              id: 'stepUpIntermediatePage_no_translations',
              link: `smarthelp-step-up-intermediate|${topicRefId}|${fromChannel}|no-translations-found-redirecting-to-anw`,
            });
            return dispatch(initiateANWCheck());
          }

          // Load step-up window only if translations for the selected topicId exists.
          return dispatch(loadStepUpContainer());
        }
        case 'ALLOW': {
          if ((isLoggedOutToLogin(getState()) || isRedirectedToAnw) && fromChannel === stepUpChannels.call) {
            const { topic } = getState().persistedStepUp;
            dispatch(getIdiContactChannels(topic.topicRefId, topic.topicName));
          }
          return dispatch(receivedRiskAllow());
        }
        default:
          return dispatch(receivedRiskAllow());
      }
    })
    .catch(() => {
      trackPage({
        id: 'stepUpIntermediatePage_riskFailure',
        link: `smarthelp-step-up-intermediate|${topicRefId}|${fromChannel}|${attempt}|risk-failure`,
      });

      // Do not dispatch error on second attempt.
      if (attempt !== attempts.SECOND) {
        dispatch(receiveRiskError());
      }
    });
};

/**
 * This is the start point for step up risk initiation.
 * Risk check will be performed only of the selected topic is supported for step-up
 * If not supported will be redirected / open the channel it was triggered from.
 *
 * @param fromChannel
 * @param selectedTopic
 * @param isOpenFinalIntentBeforeStepUp
 * @param attempt
 * @returns {Function}
 */
export const initiateRiskCheck = (
  fromChannel,
  selectedTopic,
  isOpenFinalIntentBeforeStepUp = false,
  attempt = attempts.FIRST,
) =>
  function (dispatch, getState) {
    // Before initializing step-up check whether chat/messageUs window is already open
    // When a chat/messageUs window is already active do not perform step-up operation.
    // If fromChannel is chat/messageUs/chatBot/liveChat and if node chat already exists return null
    if (
      (mappingRemoteConfig[fromChannel] === stepUpChannels.chat ||
        mappingRemoteConfig[fromChannel] === stepUpChannels.messageUs) &&
      isNodeChatAlreadyExists()
    ) {
      return null;
    }
    const topicRefId = (selectedTopic && selectedTopic.topicRefId) || null;

    dispatch(requestRiskCheck(fromChannel, selectedTopic));

    // Only for logged out customers when click on ChatWithUs / callUs / messageUs in contact options
    if (!Page.isLoggedIn()) {
      if (fromChannel === modalChannels.chat) {
        setChatCardClickInSession(true);

        // If final intent check is logged out chat is eligible
        if (topicRefId) {
          dispatch(requestLoggedOutChatValidation(topicRefId));
        }

        return dispatch(loadModalContainer(modalChannels.chat));
      }
      if (fromChannel === modalChannels.messageUs) {
        // If final intent check is logged out chat is eligible
        if (topicRefId) {
          dispatch(requestLoggedOutChatValidation(topicRefId));
        }

        return dispatch(loadModalContainer(modalChannels.messageUs));
      }
      if (fromChannel === modalChannels.call) {
        return dispatch(loadModalContainer(modalChannels.call));
      }
    }

    // If logged in
    if (Page.isLoggedIn() && topicRefId) {
      const contactChannel = getRiskContactChannelName(fromChannel);

      // Check step-up channel is enabled in remote config - Kill switch check
      if (isStepUpEnabled(contactChannel)) {
        const {
          persistedStepUp: {
            topic,
            anw: { isRedirectedToAnw },
          },
        } = getState();

        // If attempt is a second time which is a redirect from ANW.
        if (attempt === attempts.SECOND) {
          // If the fromChannel is from chatBot/liveChat/MessageUs open the final intent.
          if (
            (fromChannel === stepUpChannels.chatBot ||
              fromChannel === stepUpChannels.liveChat ||
              fromChannel === stepUpChannels.chat ||
              fromChannel === stepUpChannels.messageUs) &&
            isOpenFinalIntentBeforeStepUp
          ) {
            dispatch(fetchFinalTopicContent({ topic }));
          }

          // in the second attempt which is a redirect from ANW fire IDI service to check if the callus is available in IDI response or not.
          if (fromChannel === stepUpChannels.call) {
            dispatch(getIdiContactChannels(topic.topicRefId, topic.topicName));
          }

          // If there is service error during redirect to ANW UI then treat as allow to redirect the user to selected channel
          // and fire an FPTI with service error.
          if (Page.validateAnwIsRedirectError() && isRedirectedToAnw) {
            trackPage({
              id: 'stepUpIntermediatePage_anw_service_failure',
              link: `smarthelp-step-up-intermediate|${topicRefId}|${contactChannel}|anw-redirect-service-error`,
            });
            return dispatch(receivedRiskAllow());
          }

          // On Redirect from ANW whether the decision is ALLOW or STEPUP
          // we should allow the user to go the channel which was selected.
          dispatch(receivedRiskAllow());

          // Fire and forget the service call. Risk decision is ignored on redirected from ANW whether its success or error from ANW.
          return dispatch(performStepUpCall(topicRefId, contactChannel, attempt));

          // If the attempt if first time and If isOpenFinalIntentBeforeStepUp is set explicitly true
          // then will load the final intent before performing risk call.
          // Using this for chat, chatBot, MessageUs channels.
        }
        if (isOpenFinalIntentBeforeStepUp) {
          dispatch(fetchFinalTopicContent({ topic }));
        }

        return dispatch(performStepUpCall(topicRefId, contactChannel, attempt));
      }
    }

    // Default case if topic is not supported or in any error cases skip step-up and redirect to selected channel.
    return dispatch(receivedRiskAllow());
  };

// callback function after checking for callUs channel in IDI response.
const cbFunctionForCallUs = (value, dispatch) => {
  if (value) {
    dispatch(locationUpdate({ pageName: 'callUs' }));
  } else {
    dispatch(showHideCovidBcpModalAlert(true));
  }
};

// ------------------------  Common place for redirect logic ------------------------------

// Redirect to appropriate channel after step-up
export const redirectToAppropriateChannel = () => (dispatch, getState) => {
  const state = getState();
  const {
    persistedStepUp: {
      topic,
      anw: { isRedirectedToAnw },
    },
    stepUp: { fromChannel },
  } = state;
  switch (fromChannel) {
    case stepUpChannels.call: {
      // COVID-19 BCP FINAL INTENT LOGIC
      // NON IDI
      if (!Page.getIsIDIContactOptions()) {
        if (isSuppressCallUs()) {
          // Show COVID Callus alert window.
          dispatch(showHideCovidBcpModalAlert(true));
        } else {
          dispatch(locationUpdate({ pageName: fromChannel }));
        }
      }

      // IDI
      // Logged out to logged in or from ANW redirect.
      if (isLoggedOutToLogin(state) || isRedirectedToAnw) {
        // check if callUs channel is available in IDI response.
        isChannelAvailableInContactOptions(getState, dispatch, 'PHONE', cbFunctionForCallUs);
      } else {
        dispatch(locationUpdate({ pageName: fromChannel }));
      }

      // TODO:: Remove/refactor this as post chat bot eligibility API is not required in callus Page.
      if (isRedirectedToAnw) {
        dispatch(chatBotEligibilityResponse()); // Combine with Pilot user checker
      }
      break;
    }

    case stepUpChannels.liveChat:
    case stepUpChannels.messageUs:
    case stepUpChannels.chat: {
      // if from logged out to logged in or from ANW page file post chat bot eligibility call.
      if (isLoggedOutToLogin(getState()) || isRedirectedToAnw) {
        dispatch(chatBotEligibilityResponse());
      }

      const options = {
        topicRefId: topic && topic.topicRefId,
        fromChannel,
        showChat: initializeAppropriateChannel,
        reject: loadChatIneligibleModalContainer,
      };

      // Chat eligibility checker
      // IDI
      if (isIdiContactOptionsEnabled) {
        dispatch(idiChatEligibilityChecker(options.topicRefId, options.fromChannel, options.showChat, options.reject));
      } else {
        // NON IDI
        dispatch(
          nonIdiChatEligibilityChecker(options.topicRefId, options.fromChannel, options.showChat, options.reject),
        );
      }
      break;
    }

    default:
      break;
  }

  dispatch(resetStepUp());
  dispatch(closeModalContainer());
};
